/**
 * Keyboard shortcut UI: the document listener and the help overlay. The
 * registry and `useShortcuts` hook live in lib/shortcuts.ts.
 */
import { useEffect } from "react";
import { comboOf, isTyping, useShortcutRegistry, type Shortcut } from "@/lib/shortcuts";
import { useUi } from "@/state/ui";

/** Mount once in the app shell. */
export function KeyboardShortcutsListener() {
  const setShortcutsOpen = useUi((s) => s.setShortcutsOpen);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const combo = comboOf(e);
      if (combo === "?" && !isTyping(e)) {
        setShortcutsOpen(true);
        e.preventDefault();
        return;
      }
      const typing = isTyping(e);
      const entries = [...useShortcutRegistry.getState().shortcuts.values()].reverse();
      for (const s of entries) {
        if (s.key !== combo) continue;
        if (typing && !s.global) continue;
        e.preventDefault();
        s.handler(e);
        return;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setShortcutsOpen]);
  return null;
}

export function ShortcutsHelp() {
  const open = useUi((s) => s.shortcutsOpen);
  const setOpen = useUi((s) => s.setShortcutsOpen);
  const shortcuts = useShortcutRegistry((s) => s.shortcuts);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);
  if (!open) return null;
  const groups = new Map<string, Shortcut[]>();
  for (const s of shortcuts.values()) {
    const g = s.group ?? "general";
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(s);
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-20"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-label="keyboard shortcuts"
    >
      <div
        className="w-[420px] max-w-[90vw] rounded-sm border border-border bg-bg-elev p-3 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Keyboard shortcuts</h2>
          <button type="button" className="ctl" onClick={() => setOpen(false)}>
            esc
          </button>
        </div>
        {[...groups.entries()].map(([g, list]) => (
          <div key={g} className="mb-2">
            <div className="mb-1 text-2xs uppercase tracking-wide text-fg-faint">{g}</div>
            <table className="w-full text-[11px]">
              <tbody>
                {list.map((s, i) => (
                  <tr key={i}>
                    <td className="w-20 py-0.5">
                      <span className="kbd">{s.key.replace("mod+", "⌘/ctrl+")}</span>
                    </td>
                    <td className="py-0.5 text-fg-muted">{s.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {groups.size === 0 && <div className="text-fg-muted">No shortcuts registered on this view.</div>}
      </div>
    </div>
  );
}
